import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  formatLatexPreview
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ===== */

const within = (x, y) => Math.abs(x - y) < 0.01;

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const r = a % b;
    a = b;
    b = r;
  }
  return a;
}

function simplifyFrac(frac) {
  let { num, den } = frac;
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fracToLatex(frac) {
  const { num, den } = simplifyFrac(frac);
  if (den === 1) return `${num}`;
  const sign = num * den < 0 ? "-" : "";
  const absNum = Math.abs(num);
  const absDen = Math.abs(den);
  return `${sign}\\dfrac{${absNum}}{${absDen}}`;
}

// ładny zapis liczby jako ułamka mieszanego (do krótkiej odpowiedzi)
function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;
  if (frac === 0) return `${sign}${intPart}`;
  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\dfrac{${num}}{${den}}`
        : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

/* ===== GENERATOR ===== */

export function generateTask() {
  const k = randomInt(2, 4); // różnica indeksów 2..4
  const n1 = randomInt(1, 3); // 1..3
  const n2 = n1 + k;

  // losujemy dodatnie q: qNum/qDen, qDen ∈ {1,2,3,4}
  const qDen = Math.random() < 0.5 ? pick([2, 3, 4]) : 1;
  let qNum;
  do {
    qNum = randomInt(2, 8);
  } while (qDen > 1 && (qNum <= qDen || gcd(qNum, qDen) !== 1));

  const qPos = qDen === 1 ? qNum : qNum / qDen;

  // losujemy a1 tak, żeby a_n były całkowite:
  // a1 = s * m * qDen^{n2-1}
  const signA1 = Math.random() < 0.5 ? -1 : 1;
  const m = randomInt(1, 5);
  const base = Math.pow(qDen, n2 - 1);
  const a1Pos = signA1 * m * base;

  const a_n1 = Math.round(a1Pos * Math.pow(qPos, n1 - 1));
  const a_n2 = Math.round(a1Pos * Math.pow(qPos, n2 - 1));

  // dokładny ułamek a_{n2} / a_{n1}
  const fracAN = simplifyFrac({ num: a_n2, den: a_n1 });

  const solutionsCount = k % 2 === 0 ? 2 : 1;
  const qNeg = solutionsCount === 2 ? -qPos : null;

  // drugi możliwy a1 (dla qNeg), jeśli istnieje
  let a1Neg = null;
  if (solutionsCount === 2) {
    const powerNeg = Math.pow(qNeg, n1 - 1);
    a1Neg = a_n1 / powerNeg; // wychodzi całkowite z konstrukcji
  }

  return {
    type: "FindA1AndQGeoTask",
    data: {
      n1,
      n2,
      k,
      a_n1,
      a_n2,
      solutionsCount,
      a1Pos,
      a1Neg,
      qPos,
      qNeg,
      qNum,
      qDen,
      fracAN
    }
  };
}

/* ===== TREŚĆ ===== */

export function renderTask({ data }) {
  const { n1, a_n1, n2, a_n2 } = data;

  return (
    <>
      <p>
        Dany jest ciąg geometryczny, w którym{" "}
        <InlineMath math={`a_{${n1}} = ${a_n1}`} /> oraz{" "}
        <InlineMath math={`a_{${n2}} = ${a_n2}`} />.
      </p>
      <p>
        Oblicz pierwszy wyraz <InlineMath math="a_1" /> i iloraz{" "}
        <InlineMath math="q" /> tego ciągu.
      </p>
      <p>
        Najpierw określ, ile par <InlineMath math="(a_1,q)" /> spełnia te
        warunki, a następnie podaj ich wartości.
      </p>
    </>
  );
}

/* ===== INPUT (NOWY LAYOUT) ===== */

export function renderInput(_, userAnswer = {}, onChange, disabled) {
  const count = userAnswer.count ?? "";

  const setField = (key) => (val) =>
    onChange({
      ...userAnswer,
      [key]: val
    });

  const rowStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
    columnGap: "1rem",
    rowGap: "0.5rem",
    alignItems: "center"
  };

  return (
    <div className="flex flex-col gap-3">
      <SelectInput
        options={[
          { value: "1", label: "jedno rozwiązanie" },
          { value: "2", label: "dwa rozwiązania" }
        ]}
        value={count}
        onChange={(e) =>
          onChange({ ...userAnswer, count: e?.target?.value ?? e })
        }
        placeholder="Wybierz liczbę rozwiązań"
        disabled={disabled}
      />

      {count === "1" && (
        <div style={rowStyle}>
          <div className="flex items-center gap-2">
            <InlineMath math={"a_1^{(1)} ="} />
            <FractionInput
              value={userAnswer.a1_1 || ""}
              onChange={setField("a1_1")}
              disabled={disabled}
              label={"a_1^{(1)}"}
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"q_1 ="} />
            <FractionInput
              value={userAnswer.q_1 || ""}
              onChange={setField("q_1")}
              disabled={disabled}
              label="q₁"
            />
          </div>
        </div>
      )}

      {count === "2" && (
        <>
          <div style={rowStyle}>
            <div className="flex items-center gap-2">
              <InlineMath math={"a_1^{(1)} ="} />
              <FractionInput
                value={userAnswer.a1_1 || ""}
                onChange={setField("a1_1")}
                disabled={disabled}
                label={"a_1^{(1)}"}
              />
            </div>
            <div className="flex items-center gap-2">
              <InlineMath math={"q_1 ="} />
              <FractionInput
                value={userAnswer.q_1 || ""}
                onChange={setField("q_1")}
                disabled={disabled}
                label="q₁"
              />
            </div>
          </div>

          <div style={{ ...rowStyle, marginTop: "0.5rem" }}>
            <div className="flex items-center gap-2">
              <InlineMath math={"a_1^{(2)} ="} />
              <FractionInput
                value={userAnswer.a1_2 || ""}
                onChange={setField("a1_2")}
                disabled={disabled}
                label={"a_1^{(2)}"}
              />
            </div>
            <div className="flex items-center gap-2">
              <InlineMath math={"q_2 ="} />
              <FractionInput
                value={userAnswer.q_2 || ""}
                onChange={setField("q_2")}
                disabled={disabled}
                label="q₂"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ===== CHECK + PUNKTACJA (0–2) ===== */

export function checkAnswer({ data }, userAnswer = {}) {
  const expectedCount = data.solutionsCount;
  const givenCount = Number(userAnswer.count);
  const maxScore = 2;

  const countCorrect = givenCount === expectedCount;
  if (!countCorrect) {
    return {
      isCorrect: false,
      score: 0,
      maxScore,
      parts: { "Ilość rozwiązań": false }
    };
  }

  const a1_1 = userAnswer.a1_1?.parsed;
  const q_1 = userAnswer.q_1?.parsed;
  const a1_2 = userAnswer.a1_2?.parsed;
  const q_2 = userAnswer.q_2?.parsed;

  const a1_1_valid = a1_1 != null && !isNaN(a1_1);
  const q_1_valid = q_1 != null && !isNaN(q_1);
  const a1_2_valid = a1_2 != null && !isNaN(a1_2);
  const q_2_valid = q_2 != null && !isNaN(q_2);

  const { a1Pos, a1Neg, qPos, qNeg } = data;

  // ===== jedno rozwiązanie =====
  if (expectedCount === 1) {
    const qOk = q_1_valid && within(q_1, qPos);
    const a1Ok = a1_1_valid && within(a1_1, a1Pos);

    let score = 0;
    if (qOk && a1Ok) score = 2;
    else if (qOk) score = 1;

    return {
      isCorrect: score === 2,
      score,
      maxScore,
      parts: {
        "Ilość rozwiązań": true,
        "Pierwszy wyraz a1": a1Ok,
        "Iloraz q": qOk
      }
    };
  }

  // ===== dwa rozwiązania =====

  const solsA1 = [a1Pos, a1Neg];
  const solsQ = [qPos, qNeg];

  function countMatches(expectedVals, givenVals) {
    const used = [false, false];
    let cnt = 0;
    for (const g of givenVals) {
      if (g == null || isNaN(g)) continue;
      for (let i = 0; i < expectedVals.length; i++) {
        if (!used[i] && within(g, expectedVals[i])) {
          used[i] = true;
          cnt++;
          break;
        }
      }
    }
    return cnt;
  }

  const correctQCount = countMatches(solsQ, [
    q_1_valid ? q_1 : null,
    q_2_valid ? q_2 : null
  ]);
  const correctA1Count = countMatches(solsA1, [
    a1_1_valid ? a1_1 : null,
    a1_2_valid ? a1_2 : null
  ]);

  const pair1_pos =
    a1_1_valid && q_1_valid && within(a1_1, a1Pos) && within(q_1, qPos);
  const pair2_neg =
    a1_2_valid && q_2_valid && within(a1_2, a1Neg) && within(q_2, qNeg);

  const pair1_neg =
    a1_1_valid && q_1_valid && within(a1_1, a1Neg) && within(q_1, qNeg);
  const pair2_pos =
    a1_2_valid && q_2_valid && within(a1_2, a1Pos) && within(q_2, qPos);

  const pairsCorrect = Math.max(
    (pair1_pos ? 1 : 0) + (pair2_neg ? 1 : 0),
    (pair1_neg ? 1 : 0) + (pair2_pos ? 1 : 0)
  );

  const pair1DisplayCorrect =
    a1_1_valid &&
    q_1_valid &&
    ((within(a1_1, a1Pos) && within(q_1, qPos)) ||
      (within(a1_1, a1Neg) && within(q_1, qNeg)));

  const pair2DisplayCorrect =
    a1_2_valid &&
    q_2_valid &&
    ((within(a1_2, a1Pos) && within(q_2, qPos)) ||
      (within(a1_2, a1Neg) && within(q_2, qNeg)));

  let score = 0;
  if (pairsCorrect === 2) {
    score = 2;
  } else if (
    pairsCorrect === 1 ||
    correctQCount === 2 ||
    correctA1Count === 2
  ) {
    score = 1;
  }

  return {
    isCorrect: score === 2,
    score,
    maxScore,
    parts: {
      "Ilość rozwiązań": true,
      "Pierwsza para (a1, q)": pair1DisplayCorrect,
      "Druga para (a1, q)": pair2DisplayCorrect
    }
  };
}

/* ===== KRÓTKA ODPOWIEDŹ ===== */

export function renderAnswer({ data }) {
  const { solutionsCount, a1Pos, a1Neg, qPos, qNeg } = data;

  const qPosLatex = formatFractionLatex(qPos);
  const qNegLatex = qNeg != null ? formatFractionLatex(qNeg) : null;

  if (solutionsCount === 1) {
    return <InlineMath math={`a_1 = ${a1Pos},\\ q = ${qPosLatex}`} />;
  }

  return (
    <div style={{ display: "grid", rowGap: 2 }}>
      <InlineMath math={`a_1^{(1)} = ${a1Pos},\\ q_1 = ${qPosLatex}`} />
      <InlineMath math={`a_1^{(2)} = ${a1Neg},\\ q_2 = ${qNegLatex}`} />
    </div>
  );
}

/* ===== WYJAŚNIENIE ===== */

export function renderExplanation({ data }) {
  const {
    n1,
    n2,
    k,
    a_n1,
    a_n2,
    solutionsCount,
    qNum,
    qDen,
    fracAN,
    a1Pos,
    a1Neg
  } = data;

  const ratioANLatex = fracToLatex(fracAN);

  const qFrac = simplifyFrac({ num: qNum, den: qDen });
  const qFracLatex = fracToLatex(qFrac);
  const qNegFracLatex = fracToLatex({ num: -qFrac.num, den: qFrac.den });

  const rootBaseLatex = `\\sqrt[${k}]{${ratioANLatex}}`;

  const expN1 = n1 - 1;
  const qPowFrac = simplifyFrac({
    num: Math.pow(qFrac.num, expN1),
    den: Math.pow(qFrac.den, expN1)
  });
  const qPowFracLatex = fracToLatex(qPowFrac);

  const qNegPowFrac =
    solutionsCount === 2
      ? simplifyFrac({
          num: Math.pow(-qFrac.num, expN1),
          den: Math.pow(qFrac.den, expN1)
        })
      : null;
  const qNegPowFracLatex =
    solutionsCount === 2 && qNegPowFrac ? fracToLatex(qNegPowFrac) : null;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Zapisujemy dane wyrazy ciągu.
      </p>
      <BlockMath math={`a_{${n1}} = a_1 \\cdot q^{${n1}-1} = ${a_n1}`} />
      <BlockMath math={`a_{${n2}} = a_1 \\cdot q^{${n2}-1} = ${a_n2}`} />

      <p>
        <strong>Krok 2:</strong> Dzielimy równania, żeby otrzymać równanie na{" "}
        <InlineMath math="q" />.
      </p>
      <BlockMath
        math={`\\frac{a_{${n2}}}{a_{${n1}}} = \\frac{a_1 q^{${n2}-1}}{a_1 q^{${n1}-1}} = q^{${n2}-${n1}} = q^{${k}}`}
      />
      <BlockMath
        math={`\\frac{a_{${n2}}}{a_{${n1}}} = \\frac{${a_n2}}{${a_n1}} = ${ratioANLatex}`}
      />
      <BlockMath math={`q^{${k}} = ${ratioANLatex}`} />

      {solutionsCount === 1 ? (
        <>
          <p>
            <strong>Krok 3:</strong> Różnica indeksów{" "}
            <InlineMath math="k" /> jest{" "}
            <strong>nieparzysta</strong>, więc mamy jedno rozwiązanie dla{" "}
            <InlineMath math="q" />.
          </p>
          <BlockMath math={`q = ${rootBaseLatex} = ${qFracLatex}`} />
        </>
      ) : (
        <>
          <p>
            <strong>Krok 3:</strong> Różnica indeksów{" "}
            <InlineMath math="k" /> jest <strong>parzysta</strong>, więc
            równanie ma dwa rozwiązania.
          </p>
          <BlockMath math={`q = \\pm ${rootBaseLatex}`} />
          <BlockMath
            math={`q_1 = ${qFracLatex},\\quad q_2 = ${qNegFracLatex}`}
          />
        </>
      )}

      <p>
        <strong>Krok 4:</strong> Obliczamy pierwszy wyraz{" "}
        <InlineMath math="a_1" />.
      </p>
      <BlockMath math={`a_1 = \\dfrac{a_{${n1}}}{q^{${n1}-1}}`} />
      {solutionsCount === 1 ? (
        <BlockMath
          math={`a_1 = \\dfrac{${a_n1}}{\\left(${qFracLatex}\\right)^{${expN1}}} = \\dfrac{${a_n1}}{${qPowFracLatex}} = ${a1Pos}`}
        />
      ) : (
        <>
          <BlockMath
            math={`a_1^{(1)} = \\dfrac{${a_n1}}{\\left(${qFracLatex}\\right)^{${expN1}}} = \\dfrac{${a_n1}}{${qPowFracLatex}} = ${a1Pos}`}
          />
          <BlockMath
            math={`a_1^{(2)} = \\dfrac{${a_n1}}{\\left(${qNegFracLatex}\\right)^{${expN1}}} = \\dfrac{${a_n1}}{${qNegPowFracLatex}} = ${a1Neg}`}
          />
        </>
      )}
    </div>
  );
}

/* ===== ODPOWIEDŹ UŻYTKOWNIKA ===== */

export function renderUserAnswer(userAnswer = {}) {
  const count = userAnswer.count;
  if (!count) return <i>(brak odpowiedzi)</i>;

  const rows = [];

  rows.push(
    <div key="count">
      <strong>Liczba rozwiązań:</strong>{" "}
      {count === "1" ? "jedno rozwiązanie" : "dwa rozwiązania"}
    </div>
  );

  const showPair = (idx, a1Field, qField) => {
    const a1Val = userAnswer[a1Field];
    const qVal = userAnswer[qField];

    const a1Latex = a1Val?.raw
      ? formatLatexPreview(a1Val.raw, `a_1^{(${idx})}`)
      : null;
    const qLatex = qVal?.raw
      ? formatLatexPreview(qVal.raw, `q_${idx}`)
      : null;

    return (
      <div key={`pair${idx}`} style={{ marginTop: "0.25rem" }}>
        {a1Latex ? (
          <InlineMath math={a1Latex} />
        ) : (
          <i>{`(brak wartości a₁^{(${idx})})`}</i>
        )}{" "}
        {qLatex ? (
          <InlineMath math={qLatex} />
        ) : (
          <i>{`(brak wartości q_${idx})`}</i>
        )}
      </div>
    );
  };

  if (count === "1" || count === "2") {
    rows.push(showPair(1, "a1_1", "q_1"));
  }
  if (count === "2") {
    rows.push(showPair(2, "a1_2", "q_2"));
  }

  return (
    <div style={{ marginTop: "0.5rem", display: "grid", rowGap: 4 }}>
      {rows}
    </div>
  );
}
